import React from 'react';
import {observer} from 'mobx-react';
import s from '../../../s';
import {Portal} from '../../../../util';
import {EPTemplateLineRow, PlatePreview} from '../ep-template/ep-template';
import {templateLineRows} from '../ep-template/ep-template-apply';
import {INST_PLATE_FINISH_SILVER, PERSONALIZATION_TERMS_URL, platePreviewMaxWidthPx, plateSummary, sameSize} from './inst-plate-rules';
import {INST_PLATE_INNER_ID, instPlateStore} from './inst-plate.store';
import InstPlateConfirm from './inst-plate-confirm';
import './inst-plate.scss';

/** The spec's certification text, up to the linked words. */
export const INST_PLATE_CERTIFICATION_TEXT =
    'I certify that I own or have obtained all rights, permissions, licenses, and consents necessary to use and reproduce all content I submit for personalization, including text, names, images, photographs, logos, trademarks, and likenesses. I authorize Church Hill Classics to reproduce that content to fulfill my order, accept responsibility for the content I submit, and agree to the';
export const INST_PLATE_TERMS_TEXT = 'Custom Personalization Terms';
export const INST_PLATE_NOTE = 'Fields will appear on your plate as shown in the preview above.';
/** Under the preview while s.ui.ep_overflowing (the cut-off gate of ep-template-apply): the CAF step's wording without "or font size", there is no size control here. */
export const INST_PLATE_CUT_OFF_MESSAGE = 'Text is being cut off. Shorten the text to fit within the plate.';

// The widest plate (6.25") fills its tile's outline box; the others scale from it.
const WIDEST_PLATE_IN = 6.25;

const finishThumb = (epId: number): string => (epId === INST_PLATE_FINISH_SILVER ? `https://${Portal?.CDNDomain}/images/entities/engravedplate/ep_silver_original_engravedplate.png` : `https://${Portal?.CDNDomain}/images/entities/engravedplate/ep_gold_original_engravedplate.png`);

/**
 * The personalized-plate module (INST-PLATES D6, PRO-PLATES D5) of a product whose plate templates
 * carry a plate_type: the size tiles its templates come in, the plate type of their family (With
 * Name / Without Name, or With License / Without License), the template dropdown for that choice,
 * the plate preview with the cut-off warning, the content certification and, once a template is
 * chosen, one input per template line. It takes the plate slot of EngravedPlateExtended; Add to
 * Cart runs its gate and proof (inst-plate.store.ts beforeAddToCart, inst-plate-confirm.tsx).
 */
const InstPlate = observer(() => {
    if (!s.ui.show_cust) {
        return null;
    }

    const store = instPlateStore;
    const canCollapse = s.ps.is_caf || window.innerWidth < 960;
    const size = store.size;
    const templateId = store.templateId;
    const hasTemplate = templateId > 0;
    const summary = plateSummary(size, s.ps.ep_id);
    const available = store.available;
    // A selected template that is not in the list (a size outside the four tiles) stays selectable.
    const options = hasTemplate && store.selectedTemplate && !available.some(t => t.id === templateId) ? [...available, store.selectedTemplate] : available;
    const templateError = store.showErrors && !hasTemplate;
    const certifyError = store.showErrors && !store.approved;
    const rows = hasTemplate ? templateLineRows() : [];

    return (
        <div
            className="gray-box inst-plate"
            style={{padding: 0}}
        >
            <div style={{padding: '0 12px', position: 'relative'}}>
                <div
                    role={canCollapse ? 'button' : undefined}
                    tabIndex={canCollapse ? 0 : undefined}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.currentTarget.click();
                        }
                    }}
                    className="d-none d-xl-block toggle-bs"
                    data-bs-toggle={canCollapse ? 'collapse' : ''}
                    data-bs-target={`#${INST_PLATE_INNER_ID}`}
                    aria-expanded={canCollapse ? false : undefined}
                    aria-controls={canCollapse ? INST_PLATE_INNER_ID : undefined}
                >
                    <div className="cust-desktop-head">
                        <span>Personalized Plate: </span>
                        <span className="inst-plate-summary">{summary}</span>
                    </div>
                </div>

                <div
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.currentTarget.click();
                        }
                    }}
                    className="d-block d-xl-none toggle-bs"
                    data-bs-toggle={canCollapse ? 'collapse' : ''}
                    data-bs-target={`#${INST_PLATE_INNER_ID}`}
                    aria-expanded={canCollapse ? false : undefined}
                    aria-controls={canCollapse ? INST_PLATE_INNER_ID : undefined}
                >
                    <div className="cust-flex-head">
                        <div>
                            <div
                                className="header-std flexer cust-flex2"
                                style={{paddingRight: '0.25rem'}}
                            >
                                Personalized Plate
                            </div>
                            <div className="cust-flex2">{summary}</div>
                        </div>
                        <div>
                            {s.ps.ep_id > 0 && (
                                <img
                                    className="th-h"
                                    src={finishThumb(s.ps.ep_id)}
                                    alt="Personalized Plate Thumbnail"
                                    style={{height: 48, width: 'auto'}}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div
                id={INST_PLATE_INNER_ID}
                className={canCollapse ? 'collapse' : ''}
                style={{padding: '0 12px'}}
            >
                <div className="inst-plate-body">
                    <div
                        className="inst-plate-sizes"
                        role="group"
                        aria-label="Plate size"
                    >
                        {store.sizes.map(sz => {
                            const active = sameSize(sz, size);
                            return (
                                <button
                                    key={sz.key}
                                    type="button"
                                    aria-pressed={active}
                                    className={`inst-plate-size${active ? ' active' : ''}`}
                                    data-size={sz.key}
                                    onClick={() => store.setSize(sz)}
                                >
                                    <span
                                        className="inst-plate-size-outline"
                                        aria-hidden="true"
                                    >
                                        <span
                                            className="inst-plate-size-shape"
                                            style={{width: `${Math.round((sz.width / WIDEST_PLATE_IN) * 1000) / 10}%`, aspectRatio: `${sz.width} / ${sz.height}`}}
                                        />
                                    </span>
                                    <span className="inst-plate-size-label">{sz.label}</span>
                                </button>
                            );
                        })}
                    </div>

                    <div className="inst-plate-type">
                        <span
                            className="inst-plate-type-label"
                            id="inst-plate-type-label"
                        >
                            Plate Type:
                        </span>
                        <div
                            className="ep-toggle"
                            role="group"
                            aria-labelledby="inst-plate-type-label"
                        >
                            {store.plateTypes.map(t => (
                                <button
                                    key={t.value}
                                    type="button"
                                    className={store.plateType === t.value ? 'active' : ''}
                                    aria-pressed={store.plateType === t.value}
                                    data-plate-type={t.value}
                                    onClick={() => store.setPlateType(t.value)}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={`form-floating inst-plate-template${templateError ? ' inst-plate-error' : ''}`}>
                        <select
                            id="inst-plate-template-select"
                            className="form-select"
                            value={hasTemplate ? String(templateId) : ''}
                            aria-invalid={templateError || undefined}
                            disabled={options.length === 0}
                            onChange={e => store.selectTemplate(parseInt(e.target.value, 10) || -1)}
                        >
                            <option value="">{options.length > 0 ? 'Choose a template' : 'No templates for this size'}</option>
                            {options.map(t => (
                                <option
                                    key={t.id}
                                    value={t.id}
                                >
                                    {t.name}
                                </option>
                            ))}
                        </select>
                        <label htmlFor="inst-plate-template-select">Select a Template</label>
                    </div>

                    <PlatePreview
                        className="inst-plate-preview"
                        content={hasTemplate ? s.ps.EPContent || '' : ''}
                        width={size.width}
                        height={size.height}
                        heightPt={s.ps.css?.ep_plateHeightPt}
                        maxWidthPx={platePreviewMaxWidthPx(size.width)}
                    />
                    {/* The too-wide gate (ep-template-apply measureInstPlateOverflow); every Add to Cart button and the
                        proof's Confirm are disabled meanwhile. */}
                    {s.ui.ep_overflowing && <div className="text-danger mb-4">{INST_PLATE_CUT_OFF_MESSAGE}</div>}

                    <div className={`form-check inst-plate-certify${certifyError ? ' inst-plate-error' : ''}`}>
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="inst-plate-certify"
                            checked={store.approved}
                            aria-invalid={certifyError || undefined}
                            onChange={e => store.setApproved(e.target.checked)}
                        />
                        <label
                            className="form-check-label"
                            htmlFor="inst-plate-certify"
                        >
                            {INST_PLATE_CERTIFICATION_TEXT}{' '}
                            <a
                                href={PERSONALIZATION_TERMS_URL}
                                className="link underline"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {INST_PLATE_TERMS_TEXT}
                            </a>
                            .
                        </label>
                    </div>

                    {hasTemplate && rows.length > 0 && (
                        <div className="ep">
                            <div className="ep-template inst-plate-lines">
                                {rows.map(line => (
                                    <EPTemplateLineRow
                                        key={line.number}
                                        line={line}
                                        onTemplateLineChange={store.setLine}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    {hasTemplate && <div className="inst-plate-note">{INST_PLATE_NOTE}</div>}
                </div>
            </div>

            <InstPlateConfirm />
        </div>
    );
});

export default InstPlate;
